import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Plus, Trash2, KeyRound, Users, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { listStaff, createStaff, updateStaffApi, deleteStaff } from '../../../lib/api';

// All section keys a staff user can be allowed access to (matches AdminSidebar items)
// Master list — keep in sync with `items` in AdminSidebar.jsx.
const SECTION_OPTIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'enquiries', label: 'Enquiries & Leads' },
  { key: 'users', label: 'Registered Users' },
  { key: 'chat', label: 'Live Chat' },
  { key: 'deposits', label: 'Deposit Requests' },
  { key: 'purchases', label: 'Purchases & Delivery' },
  { key: 'reviews', label: 'Reviews & Ratings' },
  { key: 'branding', label: 'Branding & Colors' },
  { key: 'header', label: 'Header' },
  { key: 'navigation', label: 'Top Navigation' },
  { key: 'sidebar', label: 'Control Panel' },
  { key: 'hero', label: 'Hero Slides' },
  { key: 'marquee', label: 'Logo Marquee & Social' },
  { key: 'gamecards', label: 'Service Cards' },
  { key: 'products', label: 'Products (B2C / B2B)' },
  { key: 'exchanges', label: 'Exchange Listings' },
  { key: 'promotions', label: 'Promotions' },
  { key: 'originals', label: 'Platforms Showcase' },
  { key: 'inplay', label: 'Top Platforms' },
  { key: 'modals', label: 'Login / Signup' },
  { key: 'footer', label: 'Footer' },
  { key: 'payment', label: 'Payment Methods' },
  { key: 'aiconfig', label: 'AI Chat Bot' },
  { key: 'botconfig', label: 'OTP Setup' },
  { key: 'botsetting', label: 'Bot Setting' },
  { key: 'settings', label: 'Settings' },
];

export default function StaffSection() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', permissions: [] });
  const [editing, setEditing] = useState(null); // { username, password, permissions }

  const reload = async () => {
    setLoading(true);
    try { setStaff(await listStaff()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const toggle = (perms, key) => perms.includes(key) ? perms.filter((p) => p !== key) : [...perms, key];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || form.password.length < 6) return toast.error('Username + 6+ char password required');
    try {
      await createStaff({ username: form.username.trim(), password: form.password, permissions: form.permissions });
      toast.success('Staff created');
      setForm({ username: '', password: '', permissions: [] });
      setShowForm(false);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const body = { permissions: editing.permissions };
    if (editing.password && editing.password.length >= 6) body.password = editing.password;
    try {
      await updateStaffApi(editing.username, body);
      toast.success('Updated');
      setEditing(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const onDelete = async (s) => {
    if (!window.confirm(`Delete staff "${s.username}"?`)) return;
    try { await deleteStaff(s.username); toast.success('Deleted'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="staff-section">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-zinc-100 flex items-center gap-2"><Users size={18} /> Staff & Permissions</CardTitle>
          <Button onClick={() => setShowForm((s) => !s)} className="bg-[color:var(--gold)] text-black hover:brightness-110" data-testid="add-staff-btn">
            <Plus size={16} className="mr-1" /> {showForm ? 'Close' : 'Add Staff'}
          </Button>
        </CardHeader>
        <CardContent>
          {showForm && (
            <form onSubmit={submit} className="p-4 mb-5 rounded-lg border border-zinc-800 bg-zinc-950 space-y-3" data-testid="staff-create-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs">Username</Label>
                  <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="staff_jane" className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="staff-username-input" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Temporary Password (they'll be forced to change on first login)</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••" className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="staff-password-input" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300 text-xs flex items-center gap-1"><Shield size={12} /> Allowed Sections</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 mt-2">
                  {SECTION_OPTIONS.map((opt) => {
                    const on = form.permissions.includes(opt.key);
                    return (
                      <button type="button" key={opt.key} onClick={() => setForm({ ...form, permissions: toggle(form.permissions, opt.key) })}
                        className={`text-[11px] px-2.5 py-1.5 rounded-md border transition ${on ? 'bg-[color:var(--gold)] border-[color:var(--gold)] text-black font-semibold' : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'}`}
                        data-testid={`staff-perm-${opt.key}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <button type="button" onClick={() => setForm({ ...form, permissions: SECTION_OPTIONS.map((o) => o.key) })} className="text-xs text-zinc-400 hover:text-zinc-200 underline mt-2">Select all</button>
                <button type="button" onClick={() => setForm({ ...form, permissions: [] })} className="text-xs text-zinc-400 hover:text-zinc-200 underline mt-2 ml-3">Clear</button>
              </div>
              <div className="flex justify-end">
                <Button type="submit" className="bg-[color:var(--gold)] text-black font-bold" data-testid="staff-create-submit-btn">Create Staff</Button>
              </div>
            </form>
          )}

          {loading ? <div className="text-zinc-500 text-sm p-4">Loading…</div> : staff.length === 0 ? (
            <div className="text-zinc-500 text-sm p-6 text-center">No staff yet. Click "Add Staff" to create one.</div>
          ) : (
            <div className="space-y-2">
              {staff.map((s) => (
                <div key={s.username} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3" data-testid={`staff-row-${s.username}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-bold text-zinc-100 flex items-center gap-2">{s.username}
                        {s.must_change_password && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">temp pwd</span>}
                      </div>
                      <div className="text-zinc-500 text-[11px]">Created by {s.created_by || '—'}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setEditing({ username: s.username, password: '', permissions: s.permissions || [] })}
                        className="h-8 px-2 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 text-xs flex items-center gap-1" data-testid={`staff-edit-${s.username}`}>
                        <KeyRound size={12} /> Edit
                      </button>
                      <button onClick={() => onDelete(s)} className="h-8 px-2 rounded-md bg-red-600/15 hover:bg-red-600/25 border border-red-900/40 text-red-300 text-xs flex items-center gap-1" data-testid={`staff-delete-${s.username}`}>
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(s.permissions || []).length === 0 ? <span className="text-zinc-600 text-[11px]">no sections allowed</span> :
                      (s.permissions || []).map((p) => {
                        const meta = SECTION_OPTIONS.find((o) => o.key === p);
                        return <span key={p} className="text-[10px] px-1.5 py-0.5 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-300">{meta?.label || p}</span>;
                      })
                    }
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4" data-testid="staff-edit-modal">
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl max-w-lg w-full p-5 space-y-3">
            <h3 className="text-lg font-bold text-white">Edit staff: {editing.username}</h3>
            <div>
              <Label className="text-zinc-300 text-xs">Set New Password (leave blank to keep)</Label>
              <Input type="password" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="bg-zinc-900 border-zinc-800 text-zinc-100" />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">Allowed Sections</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mt-2">
                {SECTION_OPTIONS.map((opt) => {
                  const on = editing.permissions.includes(opt.key);
                  return (
                    <button type="button" key={opt.key} onClick={() => setEditing({ ...editing, permissions: toggle(editing.permissions, opt.key) })}
                      className={`text-[11px] px-2 py-1.5 rounded-md border ${on ? 'bg-[color:var(--gold)] border-[color:var(--gold)] text-black font-semibold' : 'bg-zinc-900 border-zinc-800 text-zinc-300'}`}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button onClick={() => setEditing(null)} variant="ghost" className="text-zinc-400">Cancel</Button>
              <Button onClick={saveEdit} className="bg-[color:var(--gold)] text-black font-bold" data-testid="staff-edit-save-btn">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { SECTION_OPTIONS };
